/**
 * MarketingInitiativePageV2 — redesigned Marketing Initiatives list.
 *
 * Layout: single LibTable section. No drag-to-reorder (initiatives
 * have no sort_order). Inline Edit + Activate / Deactivate +
 * Delete actions. The destructive ones still gate behind Swal
 * confirmations to match v1.
 *
 * Columns: Code · Users · Title + description · Unlock duration ·
 * Status · Created on · Actions.
 *
 * Data source: `state.initiatives.initiatives` via
 * `fetchAllMarketingInitiatives`.
 */
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import {
  fetchAllMarketingInitiatives,
  enableDisableMarketingInitiative,
  deleteMarketingInitiative,
} from '../../../../actions/marketingInitiativeActions';
import LoadingBackdrop from '../../../Common/LoadingBackdrop';
import {
  PageHeader,
  Button,
  LibTable,
  StatusPill,
  IconButton,
  cn,
} from '../../../redesign';

/* ─── Icons ────────────────────────────────────────────────────────── */

const PlusSign = (
  <span style={{ fontSize: 16, lineHeight: 1 }} aria-hidden>+</span>
);

const EditIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.12 2.12 0 0 1 3 3L12 15l-4 1 1-4z" />
  </svg>
);

const PowerIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18.36 6.64a9 9 0 1 1-12.73 0" />
    <line x1="12" y1="2" x2="12" y2="12" />
  </svg>
);

const TrashIconSvg = (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
    <path d="M10 11v6M14 11v6" />
    <path d="M9 6V4a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2" />
  </svg>
);

/* ─── Helpers ──────────────────────────────────────────────────────── */

function formatDate(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString(undefined, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    return '';
  }
}

/* ─── Main ─────────────────────────────────────────────────────────── */

export default function MarketingInitiativePageV2() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loading = useSelector((s) => s.initiatives.loading);
  const initiatives = useSelector((s) => s.initiatives.initiatives);

  useEffect(() => {
    dispatch(fetchAllMarketingInitiatives());
  }, [dispatch]);

  const handleDelete = (id, name) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `Delete "${name}"? This can't be undone.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) dispatch(deleteMarketingInitiative(id));
    });
  };

  const handleToggleActive = (id, isActive) => {
    Swal.fire({
      title: 'Are you sure?',
      text: `You will ${isActive ? 'deactivate' : 'activate'} this marketing initiative!`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: `Yes, ${isActive ? 'deactivate' : 'activate'} it!`,
      cancelButtonText: 'No, cancel',
    }).then((result) => {
      if (result.value) dispatch(enableDisableMarketingInitiative(id, !isActive));
    });
  };

  const total = Array.isArray(initiatives) ? initiatives.length : undefined;
  const totalLabel = total != null ? `${total.toLocaleString()} total` : undefined;

  return (
    <div className="bg-bg min-h-full">
      <LoadingBackdrop open={!!loading} />
      <PageHeader
        eyebrow="Engagement & Promotions"
        title="Marketing Initiatives"
        total={totalLabel}
        actions={
          <Link to="/marketing-initiatives/create" className="no-underline">
            <Button variant="coral" leadingIcon={PlusSign}>
              Add Initiative
            </Button>
          </Link>
        }
      />

      <div style={{ padding: '0 28px 32px' }}>
        {!Array.isArray(initiatives) || initiatives.length === 0 ? (
          <div
            className="bg-surface border border-sand text-text-muted italic text-center"
            style={{ borderRadius: 12, padding: '40px 16px', fontSize: 13 }}
          >
            No initiatives yet — add one to get started.
          </div>
        ) : (
          <LibTable
            fixed
            className="border border-sand"
            style={{ borderRadius: 12, overflow: 'hidden' }}
          >
            <colgroup>
              {/* code */}
              <col style={{ width: '12%' }} />
              {/* user count */}
              <col style={{ width: 80 }} />
              {/* title + desc */}
              <col />
              {/* unlock */}
              <col style={{ width: 110 }} />
              {/* status */}
              <col style={{ width: '12%' }} />
              {/* created */}
              <col style={{ width: 120 }} />
              {/* actions */}
              <col style={{ width: 140 }} />
            </colgroup>
            <LibTable.Head>
              <tr>
                <LibTable.Th>Code</LibTable.Th>
                <LibTable.NumTh>Users</LibTable.NumTh>
                <LibTable.Th>Title &amp; Description</LibTable.Th>
                <LibTable.NumTh>Unlock</LibTable.NumTh>
                <LibTable.CenterTh>Status</LibTable.CenterTh>
                <LibTable.Th>Created</LibTable.Th>
                <LibTable.Th style={{ textAlign: 'right' }}>Actions</LibTable.Th>
              </tr>
            </LibTable.Head>
            <tbody>
              {initiatives.map((i) => {
                const inactive = !i.is_active;
                return (
                  <LibTable.Row key={`init-${i.id}`} inactive={inactive}>
                    <LibTable.Td>
                      <span
                        className="font-mono text-navy"
                        style={{ fontSize: 12.5 }}
                      >
                        {i.code}
                      </span>
                    </LibTable.Td>
                    <LibTable.NumTd muted={!Number(i.user_count)}>
                      {Number(i.user_count) || 0}
                    </LibTable.NumTd>
                    <LibTable.Td>
                      <Link
                        to={`/marketing-initiatives/${i.id}/edit`}
                        className="font-semibold text-navy no-underline hover:text-teal-green-dark block truncate"
                        style={{ fontSize: 13.5, lineHeight: 1.25 }}
                        title={i.name || ''}
                      >
                        {i.name || 'Untitled'}
                      </Link>
                      {i.description && (
                        <div
                          className="text-text-muted truncate"
                          style={{ fontSize: 11.5, marginTop: 2 }}
                          title={i.description}
                        >
                          {i.description}
                        </div>
                      )}
                    </LibTable.Td>
                    <LibTable.NumTd>
                      <span style={{ fontSize: 12.5 }}>
                        {Number(i.default_unlock_duration) || 0}
                        <span className="text-text-muted" style={{ fontSize: 10.5, marginLeft: 4 }}>
                          {Number(i.default_unlock_duration) === 1 ? 'day' : 'days'}
                        </span>
                      </span>
                    </LibTable.NumTd>
                    <LibTable.CenterTd>
                      <StatusPill active={!inactive} />
                    </LibTable.CenterTd>
                    <LibTable.Td>
                      <span
                        className="font-mono text-text-muted"
                        style={{ fontSize: 11.5 }}
                      >
                        {formatDate(i.created_at)}
                      </span>
                    </LibTable.Td>
                    <LibTable.Td style={{ textAlign: 'right' }}>
                      <div
                        className={cn(
                          'inline-flex opacity-0 transition-opacity duration-150',
                          'group-hover:opacity-100',
                        )}
                        style={{ gap: 4 }}
                      >
                        <IconButton
                          size="sm"
                          aria-label={i.is_active ? 'Deactivate' : 'Activate'}
                          title={i.is_active ? 'Deactivate' : 'Activate'}
                          onClick={() => handleToggleActive(i.id, i.is_active)}
                        >
                          {PowerIconSvg}
                        </IconButton>
                        <Link
                          to={`/marketing-initiatives/${i.id}/edit`}
                          aria-label={`Edit ${i.name || i.code}`}
                        >
                          <IconButton size="sm" aria-label="Edit">{EditIconSvg}</IconButton>
                        </Link>
                        <IconButton
                          size="sm"
                          aria-label="Delete"
                          title="Delete"
                          onClick={() => handleDelete(i.id, i.name || i.code)}
                        >
                          {TrashIconSvg}
                        </IconButton>
                      </div>
                    </LibTable.Td>
                  </LibTable.Row>
                );
              })}
            </tbody>
          </LibTable>
        )}
      </div>
    </div>
  );
}
